import { SETTINGS_KEYS, environment } from '../config/environment'
import i18n from '../i18n/config'
import { authService } from './auth.service'
import { PermissionAction, PermissionType, permissionsService } from './permissions.service'
import { settingsService } from './settings.service'
import { toastService } from './toast.service'

interface WebSocketMessage {
  type: string
  data: any
}

interface WebsocketProgressMessage {
  filename?: string
  task_id?: string
  current_progress?: number
  max_progress?: number
  status?: string
  message?: string
  document_id?: number
  owner_id?: number
  organization_id?: number
  users_can_view?: number[]
  groups_can_view?: number[]
  workflow_id?: number
  workflow_name?: string
  trigger_type?: number
  document_title?: string
}

interface DocumentStatus {
  filename: string
  documentId?: number
  message?: string
  taskId?: string
  phase?: string
  currentPhaseProgress?: number
  currentPhaseMaxProgress?: number

  getProgress?(): number
}

enum WebsocketStatusType {
  STATUS_UPDATE = 'status_update',
  DOCUMENTS_DELETED = 'documents_deleted',
  LICENSE_EXPIRED = 'license_expired',
  SESSION_REPLACED = 'session_replaced',
  USER_LIMIT_ENFORCED = 'user_limit_enforced',
  WORKFLOW_EXECUTED = 'workflow_executed',
  DOCUMENT_ASSIGNED = 'document_assigned',
}

interface DocumentAssignedMessage {
  document_id?: number
  document_title?: string
  organization_id?: number
  assigned_user_ids?: number[]
  assigned_group_ids?: number[]
  performed_by_username?: string
  document_url?: string
}

class WebSocketService {
  private ws: WebSocket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectInterval = 5000
  private isConnected = false
  private documentStatuses: Map<string, DocumentStatus> = new Map()
  private autoOpenTaskIds = new Set<string>()
  private navigateCallback: ((path: string) => void) | null = null
  private locationCallback: (() => string) | null = null

  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return
    }

    const isAuth = authService.isAuthenticated()
    if (!isAuth) {
      return
    }

    // Connect without token; backend uses session auth for WebSocket when no token in URL.
    // Auth token is only fetched when user clicks "Show token" in Token Authentication input.
    const wsUrl = `${environment.webSocketProtocol}//${environment.webSocketHost}${environment.webSocketBaseUrl}status/`

    try {
      this.ws = new WebSocket(wsUrl)

      this.ws.onopen = () => {
        this.isConnected = true
        this.reconnectAttempts = 0
      }

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data)
          this.handleMessage(message)
        } catch (error) {
          console.error('[WebSocketService] Error parsing message:', error, event.data)
        }
      }

      this.ws.onclose = (event) => {
        this.isConnected = false
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect()
        }
      }

      this.ws.onerror = (error) => {
        console.error('[WebSocketService] WebSocket error:', error)
        this.isConnected = false
      }
    } catch (_error) {
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect()
      }
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++

      setTimeout(() => {
        this.connect().catch((error) => {
          console.error('[WebSocketService] ❌ Reconnect failed:', error)
        })
      }, this.reconnectInterval)
    } else {
    }
  }

  setNavigateCallback(callback: (path: string) => void): void {
    this.navigateCallback = callback
  }

  setLocationCallback(callback: () => string): void {
    this.locationCallback = callback
  }

  markTaskForAutoOpen(taskId: string): void {
    if (taskId) {
      this.autoOpenTaskIds.add(taskId)
    }
  }

  isTaskMarkedForAutoOpen(taskId?: string): boolean {
    return Boolean(taskId && this.autoOpenTaskIds.has(taskId))
  }

  private showNotification(key: string): boolean {
    const suppressOnDashboard = settingsService.get(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_SUPPRESS_ON_DASHBOARD)
    if (suppressOnDashboard && this.locationCallback) {
      const currentPath = this.locationCallback()
      if (currentPath === '/dashboard' || currentPath.startsWith('/dashboard/')) {
        return false
      }
    }
    return settingsService.get(key) === true
  }

  private showWorkflowExecutedNotification(): boolean {
    const suppressOnDashboard = settingsService.get(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_SUPPRESS_ON_DASHBOARD)
    if (suppressOnDashboard && this.locationCallback) {
      const currentPath = this.locationCallback()
      if (currentPath === '/dashboard' || currentPath.startsWith('/dashboard/')) {
        return false
      }
    }
    return settingsService.getBoolean(SETTINGS_KEYS.NOTIFICATIONS_WORKFLOW_EXECUTED, true)
  }

  private handleMessage(message: WebSocketMessage): void {
    switch (message.type) {
      case WebsocketStatusType.STATUS_UPDATE:
        this.handleStatusUpdate(message.data as WebsocketProgressMessage)
        break
      case WebsocketStatusType.DOCUMENTS_DELETED:
        if (
          message.data?.organization_id != null &&
          message.data.organization_id !== settingsService.getCurrentOrganizationId()
        ) {
          break
        }
        break
      case WebsocketStatusType.LICENSE_EXPIRED:
      case 'license_expired':
        this.handleLicenseExpired(message.data)
        break
      case WebsocketStatusType.SESSION_REPLACED:
      case 'session_replaced':
        this.handleSessionReplaced(message.data)
        break
      case WebsocketStatusType.USER_LIMIT_ENFORCED:
      case 'user_limit_enforced':
        this.handleUserLimitEnforced(message.data)
        break
      case WebsocketStatusType.WORKFLOW_EXECUTED:
      case 'workflow_executed':
        this.handleWorkflowExecuted(message.data as WebsocketProgressMessage)
        break
      case WebsocketStatusType.DOCUMENT_ASSIGNED:
      case 'document_assigned':
        this.handleDocumentAssigned(message.data as DocumentAssignedMessage)
        break
      default:
        // If type is 'license_expired' but not in enum, try to process it
        if (message.type === 'license_expired') {
          this.handleLicenseExpired(message.data)
        }
    }
  }

  private handleUserLimitEnforced(data: any): void {
    const user = permissionsService.getCurrentUser()
    if (!user || !user.is_superuser) {
      return
    }

    const deactivated = Number(data?.deactivated || 0)
    const maxUsers = data?.maxUsers
    const usernames: string[] = Array.isArray(data?.deactivatedUsernames) ? data.deactivatedUsernames : []

    const suffix = usernames.length ? ` (${usernames.join(', ')})` : ''
    toastService.showError(
      i18n.t('license_user_limit_enforced_message', {
        count: deactivated,
        maxUsers: maxUsers ?? '-',
      }) + suffix,
      null,
      12000
    )
  }

  private handleSessionReplaced(data: any): void {
    import('./broadcast.service')
      .then(({ broadcastService }) => {
        broadcastService.broadcast('logout', { ...data, reason: 'session_replaced' })
      })
      .catch((error) => {
        console.error('[WebSocketService] Failed to import broadcastService:', error)
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login?session=replaced'
        }
      })
  }

  private handleLicenseExpired(data: any): void {
    // Import broadcastService dynamically to avoid circular dependency
    import('./broadcast.service')
      .then(({ broadcastService }) => {
        // Broadcast logout event to log out the user
        broadcastService.broadcast('logout', { ...data, reason: 'license_expired' })
      })
      .catch((error) => {
        console.error('[WebSocketService] Failed to import broadcastService:', error)
        // Fallback: directly redirect to login
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login?license=expired'
        }
      })
  }

  private canViewMessage(messageData: WebsocketProgressMessage): boolean {
    const user = permissionsService.getCurrentUser()
    if (!user) return false

    if (messageData.organization_id != null) {
      const currentOrgId = settingsService.getCurrentOrganizationId()
      if (currentOrgId != null && messageData.organization_id !== currentOrgId) {
        return false
      }
    } else {
      return false
    }

    // Unattended/system notifications (consume folder, auto-import, etc.) carry
    // no owner and no explicit ACL. The organization is already matched above,
    // so they are org-wide: visible to every user in the current organization.
    const hasOwner = messageData.owner_id != null
    const hasViewLists =
      (messageData.users_can_view != null && messageData.users_can_view.length > 0) ||
      (messageData.groups_can_view != null && messageData.groups_can_view.length > 0)
    if (!hasOwner && !hasViewLists) {
      return true
    }

    return (
      Boolean(user.is_superuser) ||
      (messageData.owner_id != null && messageData.owner_id === user.id) ||
      Boolean(messageData.users_can_view && messageData.users_can_view.includes(user.id)) ||
      Boolean(messageData.groups_can_view && messageData.groups_can_view.some((groupId) => user.groups?.includes(groupId)))
    )
  }

  private handleStatusUpdate(messageData: WebsocketProgressMessage): void {
    if (!this.canViewMessage(messageData)) {
      return
    }

    const taskId = messageData.task_id || ''
    const filename = messageData.filename || ''

    let status = this.documentStatuses.get(taskId || filename)
    const created = !status

    if (!status) {
      status = {
        filename: filename,
        taskId: taskId,
        phase: 'WORKING',
        currentPhaseProgress: 0,
        currentPhaseMaxProgress: 1,
        getProgress: function () {
          const phase = this.phase
          if (phase === 'STARTED') return 0.0
          if (phase === 'UPLOADING') {
            return ((this.currentPhaseProgress || 0) / (this.currentPhaseMaxProgress || 1)) * 0.2
          }
          if (phase === 'WORKING') {
            return ((this.currentPhaseProgress || 0) / (this.currentPhaseMaxProgress || 1)) * 0.8 + 0.2
          }
          if (phase === 'SUCCESS' || phase === 'FAILED') return 1.0
          return 0.0
        },
      }
      this.documentStatuses.set(taskId || filename, status)
    }

    if (messageData.document_id) {
      status.documentId = messageData.document_id
    }

    if (messageData.message) {
      status.message = messageData.message
    }

    if (messageData.current_progress !== undefined) {
      status.currentPhaseProgress = messageData.current_progress
    }

    if (messageData.max_progress !== undefined) {
      status.currentPhaseMaxProgress = messageData.max_progress
    }

    if (messageData.status) {
      status.phase = messageData.status
    } else if (!status.phase) {
      status.phase = 'WORKING'
    }

    switch (status.phase) {
      case 'STARTED':
        if (created) {
          this.handleDocumentDetected(status)
        }
        break
      case 'SUCCESS':
        this.handleDocumentConsumptionFinished(status)
        break
      case 'FAILED':
        this.handleDocumentConsumptionFailed(status)
        break
      default:
        break
    }
  }

  private handleDocumentConsumptionFinished(status: DocumentStatus): void {
    if (this.showNotification(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_SUCCESS)) {
      const canViewDocument = permissionsService.currentUserCan(PermissionAction.View, PermissionType.Document)

      const baseContent = i18n.t('document_was_added_to_docdot')
      let content = baseContent
      if (baseContent.includes('{{filename}}')) {
        content = baseContent.replace('{{filename}}', status.filename)
      } else if (baseContent.includes('Dokument je dodat')) {
        content = `Dokument ${status.filename} je dodat u DocDot.`
      } else if (baseContent.includes('Document') && baseContent.includes('was added')) {
        content = `Document ${status.filename} was added to DocDot.`
      } else {
        content = `${status.filename}: ${baseContent}`
      }

      const shouldAutoOpen = this.isTaskMarkedForAutoOpen(status.taskId)
      if (canViewDocument && status.documentId && !shouldAutoOpen) {
        toastService.show({
          content: content,
          delay: 10000,
          actionName: i18n.t('open_document'),
          action: () => {
            if (this.navigateCallback) {
              this.navigateCallback(`/documents/${status.documentId}/details`)
            } else {
              window.location.href = `/documents/${status.documentId}/details`
            }
          },
        })
      } else {
        toastService.show({
          content: content,
          delay: 10000,
        })
      }
    }
  }

  private handleDocumentConsumptionFailed(status: DocumentStatus): void {
    if (this.showNotification(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_FAILED)) {
      toastService.showError(
        `${i18n.t('could_not_add')} ${status.filename}${status.message ? `: ${status.message}` : ''}`,
        status.message ? { message: status.message } : null
      )
    }
  }

  private handleWorkflowExecuted(data: WebsocketProgressMessage): void {
    if (!this.canViewMessage(data)) {
      return
    }
    if (!this.showWorkflowExecutedNotification()) {
      return
    }

    const name = (data.workflow_name || '').trim() || i18n.t('workflow_default_name')
    const content = i18n.t('workflow_executed_message', { name })

    const canViewDocument = permissionsService.currentUserCan(PermissionAction.View, PermissionType.Document)
    const docId = data.document_id

    if (canViewDocument && docId) {
      toastService.show(
        {
          content,
          delay: 8000,
          classname: 'success',
          actionName: i18n.t('open_document'),
          action: () => {
            if (this.navigateCallback) {
              this.navigateCallback(`/documents/${docId}/details`)
            } else {
              window.location.href = `/documents/${docId}/details`
            }
          },
        },
        { popupLane: 'tail' }
      )
    } else {
      toastService.show(
        {
          content,
          delay: 8000,
          classname: 'success',
        },
        { popupLane: 'tail' }
      )
    }
  }

  private handleDocumentAssigned(data: DocumentAssignedMessage): void {
    const user = permissionsService.getCurrentUser()
    if (!user) {
      return
    }

    if (
      data.organization_id != null &&
      data.organization_id !== settingsService.getCurrentOrganizationId()
    ) {
      return
    }

    const assignedUserIds = data.assigned_user_ids ?? []
    if (!assignedUserIds.includes(user.id)) {
      return
    }

    const title = (data.document_title || '').trim() || i18n.t('workflow_default_name')
    const content = i18n.t('workflow_document_assigned_message', { title })

    const canViewDocument = permissionsService.currentUserCan(PermissionAction.View, PermissionType.Document)
    const docId = data.document_id

    if (canViewDocument && docId) {
      toastService.show(
        {
          content,
          delay: 8000,
          classname: 'success',
          actionName: i18n.t('open_document'),
          action: () => {
            if (this.navigateCallback) {
              this.navigateCallback(`/documents/${docId}/details`)
            } else {
              window.location.href = `/documents/${docId}/details`
            }
          },
        },
        { popupLane: 'tail' }
      )
    } else {
      toastService.show(
        {
          content,
          delay: 8000,
          classname: 'success',
        },
        { popupLane: 'tail' }
      )
    }
  }

  private handleDocumentDetected(status: DocumentStatus): void {
    if (!this.showNotification(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_NEW_DOCUMENT)) {
      return
    }

    const baseProcessingContent = i18n.t('document_is_being_processed_by_docdot')
    const processingContent = baseProcessingContent.includes('{{filename}}')
      ? baseProcessingContent.replace('{{filename}}', status.filename)
      : baseProcessingContent.replace('Dokument', `Dokument ${status.filename}`).replace('Document', `Document ${status.filename}`)
    toastService.show({
      content: processingContent,
      delay: 5000,
    })
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close()
      this.ws = null
      this.isConnected = false
    }
  }

  reconnectForOrgChange(): void {
    this.clearStatuses()
    toastService.clearToasts()

    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ action: 'resubscribe_org' }))
      } catch {
        this.disconnect()
        this.connect().catch(() => {})
      }
      return
    }

    this.disconnect()
    this.connect().catch(() => {})
  }

  getConnectionStatus(): boolean {
    return this.isConnected
  }

  getConsumerStatus(phase?: string): DocumentStatus[] {
    const allStatuses = Array.from(this.documentStatuses.values())
    if (phase) {
      return allStatuses.filter((s) => s.phase === phase)
    }
    return allStatuses
  }

  getConsumerStatusNotCompleted(): DocumentStatus[] {
    return Array.from(this.documentStatuses.values()).filter((s) => s.phase !== 'SUCCESS' && s.phase !== 'FAILED')
  }

  getConsumerStatusCompleted(): DocumentStatus[] {
    return Array.from(this.documentStatuses.values()).filter((s) => s.phase === 'SUCCESS' || s.phase === 'FAILED')
  }

  dismiss(status: DocumentStatus): void {
    const key = status.taskId || status.filename
    if (key) {
      this.documentStatuses.delete(key)
    }
    if (status.taskId) {
      this.autoOpenTaskIds.delete(status.taskId)
    }
  }

  dismissCompleted(): void {
    const completed = this.getConsumerStatusCompleted()
    completed.forEach((status) => {
      const key = status.taskId || status.filename
      if (key) {
        this.documentStatuses.delete(key)
      }
      if (status.taskId) {
        this.autoOpenTaskIds.delete(status.taskId)
      }
    })
  }

  clearStatuses(): void {
    this.documentStatuses.clear()
    this.autoOpenTaskIds.clear()
  }
}

export const websocketService = new WebSocketService()
